import { Agent } from "https";

import { ENVIRONMENT } from "../constants";

// @ts-ignore
import { PeekErrorWrapper } from "@daymade/common-lib/errors";
import getConfig from 'next/config';
import HttpStatusCode from 'utils/HttpStatusCode';

const { publicRuntimeConfig } = getConfig();
export const API_ENDPOINT = publicRuntimeConfig.API_ENDPOINT;

/** A possible return type of performFetchRequest if an error occurs */
export interface PerformFetchError {
  successful: false,
  reason: 'Network request failed',
}

/** Performs a fetch request. The return type of this may be PerformFetchError depending on the values provided by
 * the blob and throwOnError params.
 * 
 * The typing of this function is horrible to implement due to needing to specify the generic of the expected return
 * type, whilst also varying the return type based on the blob and throwOnError parameters
 */
export const performFetchRequest = async (
  path: string,
  params?: any,
  blob: boolean = false,
  throwOnError: boolean = false
) => {
  let res;

  try {
    const startTime = Date.now();

    res = await fetch(API_ENDPOINT + path, {
      ...params,
      agent: new Agent({
        rejectUnauthorized: ENVIRONMENT !== 'local',
      }),
    });

    if (typeof window === 'undefined') {
      console.log('FETCH:', JSON.stringify(path), Date.now() - startTime);
    }
  } catch (error) {
    console.log(
      `An error occurred while performing fetch request to ENDPOINT: ${API_ENDPOINT} PATH: ${path}`,
      error
    );
    if (throwOnError) {
      throw error;
    } else {
      return {
        successful: false,
        reason: 'Network request failed',
      };
    }
  }

  if (blob) return await res.blob();

  let json = null;
  try {
    json = await res.json();
  } catch (error) {
    console.log(`Error parsing JSON response: ${error}`);
  }

  if (res.status !== HttpStatusCode.OK) {
    console.log('API ERROR: non-200 HTTP response code received from server');
    // if (json?.error) throw new Error(json.error);
  }

  return json;
};

export const generateOptionalQueryString = (
  optionalQueryParams: Record<string, any>
) => {
  // Filter out any unset optional params
  const queryParams = Object.entries(optionalQueryParams).filter(
    ([_, optionalValue]) => !!optionalValue
  );
  if (queryParams.length === 0) {
    // No optional params have been provided
    return "";
  }

  let queryStr = "";
  queryParams.forEach(([key, value]) => {
    if (!queryStr) {
      queryStr = "?";
    } else {
      queryStr += "&";
    }
    queryStr += `${key}=${value}`;
  });
  return queryStr;
};

/** This function inlines the try..catch behaviour when calling a TS Rest API, as it can feel clunky putting try..catch
 * around an API call that stores the response in a variable.
 * 
 * It tries a TSRest API call, and returns 'error' as the status if any network or 500 errors happen.
 * 
 * The caller is expected to perform any UI cleanup for the error (e.g. setting loading states back to
 * false, or showing a friendly error message)
 * 
 * The unawaited promise should be provided as a parameter. It will be awaited by tryApi.
 */
export async function tryApi<T extends object|string|number|boolean>(apiRequestPromise: Promise<T>): Promise<{ status: 'error'; error: PeekErrorWrapper } | Awaited<T>> {
  try {
    return await apiRequestPromise;
  } catch (e) {
    return {
      status: 'error',
      // Use type assertion to PeekErrorWrapper, so that it is easier for consumers to inspect the Error
      // object 
      error: e as PeekErrorWrapper
    };
  }
}

/** Converts a primitive value as a number. If the value is not parsable, or ends up being NaN, then undefined
 * is returned, to allow easy use of null-coalescing operator (??). e.g toNumberOrUndefined(thing) ?? 4.5;
 * 
 * This functionality deliberately does not take "any" as a parameter so that arrays are not passed to this function. If
 * you really want to pass in an "any" value, then use "as any" when providing the parameter
 * 
 * @deprecated This should be moved to packages/common-lib once DRIP is completed
 */
export function toNumberOrUndefined(value: string|number|boolean): number | undefined {
  try {
    const parsedValue = typeof value === 'number' ? value :
      parseFloat(value as any);
    if (isNaN(parsedValue)) {
      return undefined;
    }
    return parsedValue;
  } catch (e) {
    return undefined;
  }
}